import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Chrome/Edge fire `beforeinstallprompt` when the app meets the installability
 * criteria. The event has to be captured and replayed later, because the prompt
 * may only be shown in response to a user gesture.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

@Injectable({ providedIn: 'root' })
export class PwaInstallService {

  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private readonly installable = new BehaviorSubject<boolean>(false);

  constructor() {
    window.addEventListener('beforeinstallprompt', event => {
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.installable.next(true);
    });

    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      this.installable.next(false);
    });
  }

  /** True once the browser has offered an install prompt we can replay. */
  get installable$(): Observable<boolean> {
    return this.installable.asObservable();
  }

  /** Already launched from the home screen, so there is nothing to install. */
  get isRunningStandalone(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as { standalone?: boolean }).standalone === true;
  }

  /**
   * Safari never fires `beforeinstallprompt`; installing there is a manual
   * "Share -> Add to Home Screen", so the app has to explain it instead.
   */
  get needsIosInstructions(): boolean {
    const ua = window.navigator.userAgent;
    const isIos = /iPad|iPhone|iPod/.test(ua)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    return isIos && !this.isRunningStandalone;
  }

  async install(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (!this.deferredPrompt) {
      return 'unavailable';
    }
    await this.deferredPrompt.prompt();
    const { outcome } = await this.deferredPrompt.userChoice;
    // The captured event can only be used once.
    this.deferredPrompt = null;
    this.installable.next(false);
    return outcome;
  }
}
